/**
 * @file     routes.ts
 * @purpose  Defines all app route path constants as a typed record, plus the propertyRoute helper and AppRouteKey/AppRoutePath types.
 */
export const routes = {
  home: '/',

  firstTimeBuyerGuide: '/first-time-buyer-guide',
  condoBuyingVancouver: '/condo-buying-vancouver',
  movingToVancouver: '/moving-to-vancouver',
  vancouverRealEstateOpportunities: '/vancouver-real-estate-opportunities',

  sellMyHomeVancouver: '/sell-house/vancouver',
  sellHouse: '/sell-house',
  sellOffMarket: '/sell-off-market',
  shouldISellNow: '/should-i-sell-now',
  depreciationReport: '/depreciation-report',
  findTenants: '/find-tenants',
  /** Where /find-tenants 308s; link here directly. */
  findTenantsLanding: '/deal-finder/landlord',
  /** The rental map search: its own page, never /properties?mode=rent. */
  rent: '/rent',
  rentOutYourPlace: '/rent-out-your-place',
  rentalsListed: '/rentals/listed',

  marketUpdate: '/market-update',
  marketInsight: '/market-insight',
  blog: '/blog',
  marketReport: '/market-report',
  homesByCity: '/homes',
  priceDrops: '/price-drops',
  buildings: '/buildings',
  rentVsBuy: '/rent-vs-buy',
  rentVsMortgage: '/rent-vs-mortgage',
  shortTermRentalHomes: '/short-term-rental-homes',
  neighborhoodGuides: '/find-your-neighbourhood-vancouver',

  smartMatch: '/mls-property-matcher',
  propertyFinder: '/property-finder',
  dealFinder: '/deal-finder',
  tools: '/tools',
  compare: '/compare',
  homeValue: '/sell-house',
  mortgageCalculator: '/mortgage-calculator',
  homeSaleProceedsCalculator: '/home-sale-proceeds-calculator',
  mortgageStressTest: '/mortgage-stress-test',
  mortgagePreApproval: '/mortgage-pre-approval-bc',
  preApprovalCheck: '/pre-approval-check',
  links: '/links',
  firstTimeBuyerPrograms: '/first-time-buyer-programs',

  aboutRay: '/about-ray',
  contact: '/contact',
  privacy: '/privacy',
  terms: '/terms',
  disclosure: '/disclosure',
  reltaAgentPrivacy: '/relta-agent-privacy',
  reltaAgentTerms: '/relta-agent-terms',
  tryCmaTool: '/try-cma-tool',
  reltaAgentApp: '/relta-agent-app',
  meet: '/meet',
  signIn: '/sign-in',
  signUp: '/sign-up',
  academy: '/learn-about-bc-real-estate',
  academyBuyer: '/learn-about-bc-real-estate/buyer',
  academySeller: '/learn-about-bc-real-estate/seller',
  academyLandlord: '/learn-about-bc-real-estate/landlord',
  academyPractice: '/learn-about-bc-real-estate/practice',
  academyWelcome: '/learn-about-bc-real-estate/welcome',


  dailyHotDeals: '/daily-hot-deals',
  hotDeals: '/hot-deals',
  presales: '/presales',
  homeWorth: '/what-is-my-home-worth',
  recentlySold: '/recently-sold',
  bestTimeToSell: '/best-time-to-sell',
  costOfBuying: '/cost-of-buying-a-home-bc',
  bcPropertyTaxes: '/bc-property-taxes',
  bcHomeFlippingTax: '/bc-home-flipping-tax',
  speculationVacancyTax: '/speculation-and-vacancy-tax-bc',
  propertyTransferTaxCalculator: '/bc-property-transfer-tax-calculator',
  gstNewHomeRebate: '/gst-new-home-rebate-bc',
  closingCosts: '/closing-costs-bc',
  affordability: '/what-can-i-afford',

  buy: '/buy',
  sell: '/sell-house',
  properties: '/properties',
  neighbourhoodMap: '/neighbourhood-map',
  britishColumbia: '/british-columbia',

  // Backward-compatible aliases for older route keys.
  firstTimeBuyers: '/first-time-buyer-guide',
  condoBuyers: '/condo-buying-vancouver',
  relocation: '/moving-to-vancouver',
  opportunities: '/vancouver-real-estate-opportunities'
} as const;

/**
 * Canonical curated detail URL (ADR-016). Was `/properties/featured/{slug}`
 * pre-ADR-016; that legacy URL now 308-redirects here.
 */
export const propertyRoute = (slug: string) => `/properties/${slug}` as const;

/**
 * MLS detail URL using the raw 32-char hex listing key. Server 308-redirects
 * this to `propertyCanonicalRoute` when a `listing_slug` exists.
 */
export const propertyMlsRoute = (key: string) => `/properties/${key}` as const;

/**
 * Canonical detail URL (ADR-016). One URL per listing. Pass the curated slug
 * or MLS `listing_slug`; falls back to a raw listing_key for MLS rows that
 * have not yet been backfilled.
 */
export const propertyCanonicalRoute = (slugOrKey: string) =>
  `/properties/${slugOrKey}` as const;

/**
 * Sign in, then return to `current` (path plus search). The auth pages
 * themselves and the home page get the bare route.
 */
export const signInRoute = (current: string): string => {
  const path = current.split(/[?#]/)[0];
  const onAuthPage = [routes.signIn, routes.signUp].some((auth) => path === auth || path.startsWith(`${auth}/`));
  if (!current.startsWith('/') || current.startsWith('//') || onAuthPage || current === '/') return routes.signIn;
  return `${routes.signIn}?${new URLSearchParams({ redirect: current }).toString()}`;
};

export type AppRouteKey = keyof typeof routes;
export type AppRoutePath = (typeof routes)[AppRouteKey];
